// OrbitalCoverageCanvas.jsx — 3D orbital coverage visualization
// Shows EP-ADCS cross-track shift accumulated across multiple orbit passes
// and how it achieves global ground coverage

import { useState, useRef, useEffect, useCallback } from "react";
import { useTheme, mono } from "./appHelpers";

const CW = 820, CH = 460;
const R   = 165;       // globe pixel radius
const GCX = R + 28;    // globe center X
const GCY = CH / 2;    // globe center Y
const MFONT = "'JetBrains Mono', monospace";

// Physical constants
const R_E_KM  = 6371;
const ALT_KM  = 500;
const R_ORB_M = (R_E_KM + ALT_KM) * 1000;
const MU      = 3.986e14;
const T_ORB   = 2 * Math.PI * Math.sqrt(Math.pow(R_ORB_M, 3) / MU); // ~5676 s
const EARTH_ROT_RATE = 2 * Math.PI / 86400; // rad/s

// View direction — orthographic from (lat=25°, lon=-15°)
const VL = 25  * Math.PI / 180;
const VN = -15 * Math.PI / 180;

// Orthographic projection: lat/lon → screen (sx, sy) + visibility
function project(latDeg, lonDeg) {
  const lat = latDeg * Math.PI / 180;
  const lon = lonDeg * Math.PI / 180;
  // ECEF unit sphere
  let x = Math.cos(lat) * Math.cos(lon);
  let y = Math.cos(lat) * Math.sin(lon);
  let z = Math.sin(lat);
  // Rotate by -VN around Z
  const c1 = Math.cos(-VN), s1 = Math.sin(-VN);
  let x1 = x * c1 - y * s1;
  let y1 = x * s1 + y * c1;
  let z1 = z;
  // Rotate by -(90°-VL) around X to tilt
  const tilt = VL - Math.PI / 2;
  const c2 = Math.cos(tilt), s2 = Math.sin(tilt);
  let x2 = x1;
  let y2 = y1 * c2 - z1 * s2;
  let z2 = y1 * s2 + z1 * c2;
  return { sx: GCX + R * x2, sy: GCY - R * y2, visible: z2 > -0.08, z: z2 };
}

// Generate ground track for one orbit pass
// incDeg: orbital inclination, raanDeg: right ascension of ascending node
// Returns array of {lat, lon} — Earth rotation already subtracted
function groundTrack(incDeg, raanDeg, nPts = 200) {
  const inc = incDeg * Math.PI / 180;
  const raan = raanDeg * Math.PI / 180;
  const angSpeed = 2 * Math.PI / T_ORB;
  const pts = [];
  for (let i = 0; i <= nPts; i++) {
    const theta = (i / nPts) * 2 * Math.PI;
    const t = theta / angSpeed;
    // Orbital → ECI
    const cosI = Math.cos(inc), sinI = Math.sin(inc);
    const cosR = Math.cos(raan), sinR = Math.sin(raan);
    const xE = cosR * Math.cos(theta) - sinR * Math.sin(theta) * cosI;
    const yE = sinR * Math.cos(theta) + cosR * Math.sin(theta) * cosI;
    const zE = Math.sin(theta) * sinI;
    // Earth rotation offset
    const lonOff = EARTH_ROT_RATE * t * 180 / Math.PI;
    const lat = Math.asin(Math.max(-1, Math.min(1, zE))) * 180 / Math.PI;
    let lon = Math.atan2(yE, xE) * 180 / Math.PI - lonOff;
    while (lon > 180) lon -= 360;
    while (lon < -180) lon += 360;
    pts.push({ lat, lon });
  }
  return pts;
}

// Draw function — pure, no React state reads
function drawScene(ctx, opts) {
  const { Cx, nPasses, shiftKm, animPhase, animPass, showSwath, swathKm } = opts;

  ctx.fillStyle = Cx.bg;
  ctx.fillRect(0, 0, CW, CH);

  // ── Earth sphere ──────────────────────────────────────────────────────────
  ctx.beginPath();
  ctx.arc(GCX, GCY, R, 0, Math.PI * 2);
  const eg = ctx.createRadialGradient(GCX - R * 0.3, GCY - R * 0.3, 0, GCX, GCY, R);
  eg.addColorStop(0, '#0d2a4a');
  eg.addColorStop(1, '#061525');
  ctx.fillStyle = eg;
  ctx.fill();
  ctx.strokeStyle = '#1e4d7b88';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Atmosphere glow ring
  ctx.beginPath();
  ctx.arc(GCX, GCY, R + 6, 0, Math.PI * 2);
  ctx.strokeStyle = '#0e9cc020';
  ctx.lineWidth = 8;
  ctx.stroke();

  // ── Grid lines ─────────────────────────────────────────────────────────────
  for (let lat = -60; lat <= 60; lat += 30) {
    ctx.beginPath();
    let first = true;
    for (let lon = -178; lon <= 180; lon += 4) {
      const p = project(lat, lon);
      if (p.visible) {
        if (first) { ctx.moveTo(p.sx, p.sy); first = false; }
        else ctx.lineTo(p.sx, p.sy);
      } else first = true;
    }
    ctx.strokeStyle = lat === 0 ? '#ffffff18' : '#ffffff09';
    ctx.lineWidth = lat === 0 ? 0.8 : 0.4;
    ctx.stroke();
  }
  for (let lon = -150; lon <= 180; lon += 30) {
    ctx.beginPath();
    let first = true;
    for (let lat = -85; lat <= 85; lat += 4) {
      const p = project(lat, lon);
      if (p.visible) {
        if (first) { ctx.moveTo(p.sx, p.sy); first = false; }
        else ctx.lineTo(p.sx, p.sy);
      } else first = true;
    }
    ctx.strokeStyle = '#ffffff09';
    ctx.lineWidth = 0.4;
    ctx.stroke();
  }

  // Equator highlight
  ctx.beginPath();
  let firstEq = true;
  for (let lon = -178; lon <= 180; lon += 3) {
    const p = project(0, lon);
    if (p.visible) {
      if (firstEq) { ctx.moveTo(p.sx, p.sy); firstEq = false; }
      else ctx.lineTo(p.sx, p.sy);
    } else firstEq = true;
  }
  ctx.strokeStyle = '#ffffff22';
  ctx.lineWidth = 1;
  ctx.stroke();

  // ── Orbit passes ──────────────────────────────────────────────────────────
  // RAAN shift per pass (in degrees) from cross-track displacement
  const shiftRaanDeg = (shiftKm * 1000) / (R_E_KM * 1000) * (180 / Math.PI);
  const baseRaan = 10;
  const INC = 98;

  // Pre-compute all tracks
  const tracks = [];
  for (let p = 0; p < nPasses; p++) {
    tracks.push(groundTrack(INC, baseRaan + p * shiftRaanDeg));
  }

  // Draw coverage swath background first (below lines)
  if (showSwath) {
    const swathHalfDeg = (swathKm / 2) / R_E_KM * (180 / Math.PI);
    for (let pass = 0; pass < nPasses; pass++) {
      const track = tracks[pass];
      const alpha = Math.floor(30 * (1 - pass / nPasses)).toString(16).padStart(2, '0');
      // Draw thicker line as proxy for swath
      ctx.beginPath();
      let first = true;
      for (const { lat, lon } of track) {
        const pt = project(lat, lon);
        if (pt.visible && pt.z > 0) {
          if (first) { ctx.moveTo(pt.sx, pt.sy); first = false; }
          else ctx.lineTo(pt.sx, pt.sy);
        } else first = true;
      }
      ctx.strokeStyle = `#22c55e${alpha}`;
      const swathPx = Math.max(2, swathHalfDeg * 2 * R * Math.PI / 180);
      ctx.lineWidth = swathPx;
      ctx.stroke();
    }
  }

  // Draw ground track lines
  for (let pass = 0; pass < nPasses; pass++) {
    const track = tracks[pass];
    const isActive = pass === animPass;
    const fade = Math.max(0, 1 - pass / nPasses);
    const alphaHex = Math.floor(255 * (isActive ? 1 : 0.25 + fade * 0.4)).toString(16).padStart(2, '0');
    const color = isActive ? '#0e9cc0' : '#22c55e';

    ctx.beginPath();
    let first = true;
    for (const { lat, lon } of track) {
      const pt = project(lat, lon);
      if (pt.visible && pt.z > 0) {
        if (first) { ctx.moveTo(pt.sx, pt.sy); first = false; }
        else ctx.lineTo(pt.sx, pt.sy);
      } else first = true;
    }
    ctx.strokeStyle = `${color}${alphaHex}`;
    ctx.lineWidth = isActive ? 2 : 1;
    ctx.stroke();
  }

  // ── Animated satellite ──────────────────────────────────────────────────
  const satTrack = tracks[animPass];
  if (satTrack) {
    const idx = Math.min(satTrack.length - 1, Math.floor(animPhase * satTrack.length));
    const { lat, lon } = satTrack[idx];
    const sp = project(lat, lon);
    if (sp.visible && sp.z > 0) {
      // Glow
      const glow = ctx.createRadialGradient(sp.sx, sp.sy, 0, sp.sx, sp.sy, 14);
      glow.addColorStop(0, '#f59e0b80');
      glow.addColorStop(1, '#f59e0b00');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(sp.sx, sp.sy, 14, 0, Math.PI * 2);
      ctx.fill();
      // Dot
      ctx.beginPath();
      ctx.arc(sp.sx, sp.sy, 5, 0, Math.PI * 2);
      ctx.fillStyle = '#f59e0b';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(sp.sx, sp.sy, 5, 0, Math.PI * 2);
      ctx.strokeStyle = '#ffffff88';
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }

  // ── Stats panel (right of globe) ───────────────────────────────────────
  const rx = GCX + R + 36;
  const ry0 = 28;
  const rw = CW - rx - 12;

  function statBox(label, value, unit, color, y, subLabel) {
    ctx.fillStyle = color + '18';
    ctx.beginPath();
    ctx.roundRect?.(rx, y, rw, 54, 6) ?? ctx.rect(rx, y, rw, 54);
    ctx.fill();
    ctx.strokeStyle = color + '44';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect?.(rx, y, rw, 54, 6) ?? ctx.rect(rx, y, rw, 54);
    ctx.stroke();
    ctx.font = `bold 7px ${MFONT}`;
    ctx.fillStyle = color;
    ctx.textAlign = 'left';
    ctx.fillText(label, rx + 10, y + 14);
    ctx.font = `bold 20px ${MFONT}`;
    ctx.fillStyle = color;
    ctx.fillText(value, rx + 10, y + 36);
    ctx.font = `9px ${MFONT}`;
    ctx.fillStyle = Cx.muted;
    ctx.fillText(unit, rx + 10 + ctx.measureText(value).width + 6, y + 36);
    if (subLabel) {
      ctx.font = `7px ${MFONT}`;
      ctx.fillStyle = Cx.muted;
      ctx.fillText(subLabel, rx + 10, y + 48);
    }
  }

  const shiftMperOrbit = shiftKm * 1000;
  const equatorialKm   = 2 * Math.PI * R_E_KM;
  const passesToCover  = Math.ceil(equatorialKm / shiftKm);
  const daysToCover    = passesToCover / (86400 / T_ORB);

  const gap = 62;
  statBox('ORBIT PERIOD',               T_ORB.toFixed(0),           's',            '#0e9cc0', ry0,       null);
  statBox('CROSS-TRACK SHIFT / ORBIT',  shiftMperOrbit.toFixed(1),  'm / orbit',    '#a855f7', ry0 + gap, null);
  statBox('PASSES TO COVER EQUATOR',    passesToCover.toFixed(0),   'orbits',       '#f59e0b', ry0 + gap * 2, `equatorial circumf ${equatorialKm.toFixed(0)} km`);
  statBox('DAYS TO FULL COVERAGE',      daysToCover.toFixed(1),     'days',         '#22c55e', ry0 + gap * 3, `${(86400/T_ORB).toFixed(1)} orbits/day`);

  // Current pass indicator
  const cpY = ry0 + gap * 4 + 8;
  ctx.font = `bold 8px ${MFONT}`;
  ctx.fillStyle = Cx.muted;
  ctx.textAlign = 'left';
  ctx.fillText(`ORBIT  ${animPass + 1} / ${nPasses}`, rx + 10, cpY);
  const barW = rw - 20;
  ctx.fillStyle = Cx.bg;
  ctx.fillRect(rx + 10, cpY + 6, barW, 6);
  ctx.fillStyle = '#0e9cc040';
  ctx.fillRect(rx + 10, cpY + 6, barW, 6);
  // Pass progress
  ctx.fillStyle = '#0e9cc0';
  ctx.fillRect(rx + 10, cpY + 6, barW * ((animPass + animPhase) / nPasses), 6);
  // Phase in orbit
  ctx.font = `7px ${MFONT}`;
  ctx.fillStyle = Cx.muted;
  ctx.fillText(`phase in orbit: ${(animPhase * 360).toFixed(1)}°`, rx + 10, cpY + 22);

  // Legend
  const legY = CH - 48;
  [
    { col: '#0e9cc0', label: 'Active orbit track' },
    { col: '#22c55e', label: `Previous passes (${nPasses - 1} shown)` },
    { col: '#f59e0b', label: 'Satellite position' },
  ].forEach(({ col, label }, i) => {
    const lx = rx + 10 + i * ((rw - 20) / 3);
    ctx.beginPath();
    ctx.arc(lx + 5, legY + 4, 4, 0, Math.PI * 2);
    ctx.fillStyle = col;
    ctx.fill();
    ctx.font = `7px ${MFONT}`;
    ctx.fillStyle = Cx.muted;
    ctx.textAlign = 'left';
    ctx.fillText(label, lx + 13, legY + 8);
  });
}

export default function OrbitalCoverageCanvas({ cycleStrokeM = 0.008, tCycleS = 0.136 }) {
  const Cx = useTheme();

  // EP-ADCS natural shift per orbit
  const cyclesPerOrbit  = T_ORB / tCycleS;
  const naturalShiftM   = cycleStrokeM * cyclesPerOrbit;
  const naturalShiftKm  = naturalShiftM / 1000;

  const [shiftInput, setShiftInput]   = useState(naturalShiftKm.toFixed(3));
  const [nPasses, setNPasses]         = useState(12);
  const [showSwath, setShowSwath]     = useState(false);
  const [swathKm, setSwathKm]         = useState(10);
  const [running, setRunning]         = useState(false);

  // Animation state via refs (avoid React re-renders in tight loop)
  const animRef  = useRef({ pass: 0, phase: 0 });
  const rafRef   = useRef(null);
  const lastTsRef = useRef(null);
  const canvasRef = useRef(null);
  const CxRef    = useRef(Cx);
  CxRef.current  = Cx;

  const shiftKm = Math.max(0.001, parseFloat(shiftInput) || naturalShiftKm);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    const Cxt = CxRef.current;
    if (!canvas || !Cxt) return;
    const ctx = canvas.getContext('2d');
    const { pass, phase } = animRef.current;
    drawScene(ctx, {
      Cx: Cxt,
      nPasses,
      shiftKm,
      animPhase: phase,
      animPass: pass,
      showSwath,
      swathKm,
    });
  }, [nPasses, shiftKm, showSwath, swathKm]);

  const loop = useCallback((ts) => {
    const dt = lastTsRef.current ? Math.min((ts - lastTsRef.current) / 1000, 0.05) : 0.016;
    lastTsRef.current = ts;
    // 1 full orbit in 12 s of wall time
    const dPhase = dt / 12;
    animRef.current.phase += dPhase;
    if (animRef.current.phase >= 1) {
      animRef.current.phase -= 1;
      animRef.current.pass  = (animRef.current.pass + 1) % nPasses;
    }
    render();
    rafRef.current = requestAnimationFrame(loop);
  }, [render, nPasses]);

  useEffect(() => {
    if (running) {
      lastTsRef.current = null;
      rafRef.current = requestAnimationFrame(loop);
    } else {
      cancelAnimationFrame(rafRef.current);
    }
    return () => cancelAnimationFrame(rafRef.current);
  }, [running, loop]);

  useEffect(() => { if (!running) render(); }, [render, running]);

  const btnStyle = (active, color) => ({
    ...mono, fontSize: 9, padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
    background: active ? `${color}22` : 'transparent',
    border: `1px solid ${active ? color + '88' : (Cx?.border ?? '#1e2d3d')}`,
    color: active ? color : (Cx?.muted ?? '#4b5563'),
    letterSpacing: 1.2,
  });

  const inputStyle = {
    width: 72, padding: '3px 7px', ...mono, fontSize: 11,
    background: 'transparent', border: `1px solid ${Cx?.border ?? '#1e2d3d'}`,
    color: Cx?.text ?? '#e2e8f0', borderRadius: 4, textAlign: 'right',
  };

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={() => setRunning(r => !r)} style={btnStyle(running, '#0e9cc0')}>
          {running ? '■ PAUSE' : '▶ ANIMATE'}
        </button>
        <button onClick={() => { animRef.current = { pass: 0, phase: 0 }; render(); }}
          style={btnStyle(false, '#ef4444')}>
          ⟳ RESET
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ ...mono, fontSize: 9, color: Cx?.muted }}>SHIFT / ORBIT</span>
          <input type="number" min="0.001" max="100" step="0.01"
            value={shiftInput} onChange={e => setShiftInput(e.target.value)}
            style={inputStyle} />
          <span style={{ ...mono, fontSize: 9, color: Cx?.muted }}>km</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ ...mono, fontSize: 9, color: Cx?.muted }}>PASSES</span>
          <input type="number" min="1" max="50" step="1"
            value={nPasses} onChange={e => setNPasses(Math.max(1, parseInt(e.target.value) || 12))}
            style={{ ...inputStyle, width: 52 }} />
        </div>

        <button onClick={() => setShowSwath(s => !s)} style={btnStyle(showSwath, '#22c55e')}>
          {showSwath ? '▼ HIDE SWATH' : '▶ SHOW SWATH'}
        </button>
        {showSwath && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ ...mono, fontSize: 9, color: Cx?.muted }}>SWATH</span>
            <input type="number" min="1" max="500" step="5"
              value={swathKm} onChange={e => setSwathKm(Math.max(1, parseFloat(e.target.value) || 10))}
              style={{ ...inputStyle, width: 52 }} />
            <span style={{ ...mono, fontSize: 9, color: Cx?.muted }}>km</span>
          </div>
        )}

        <div style={{ ...mono, fontSize: 9, color: Cx?.muted, padding: '4px 10px',
          background: '#22c55e15', border: '1px solid #22c55e33', borderRadius: 6 }}>
          EP-ADCS natural: {naturalShiftKm.toFixed(3)} km/orbit
        </div>
      </div>

      {/* Canvas */}
      <div style={{
        background: Cx?.panel ?? '#0d1117',
        border: `1px solid ${Cx?.border ?? '#1e2d3d'}`,
        borderTop: '2px solid #0e9cc088',
        borderRadius: 10, overflow: 'hidden',
      }}>
        <canvas
          ref={canvasRef}
          width={CW} height={CH}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
      </div>
    </div>
  );
}
